import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Parser from 'html-react-parser';
import { useHistory } from 'react-router-dom';
import l from '../../../lang';
import './styles.scss';
import Loading from '../../common/loading';
import { scrollIntoView } from '../../../utils';
import TopDashboardSection from '../../common/top-dashboard-section';
import Message from '../../common/message';
import BasicInfo from './basic-info';
import { showUploadLogo, changeShowUpLevel, getKycLevels } from '../../../store/company-information/actions';
import OnboardingDialog from '../../common/onboarding-dialog';
import { Routes } from '../../../config/routes';
import ButtonLink from '../../common/button-link';
import { Urls } from '../../../config/urls';
import LinkDocs from '../../common/link-docs';
import Card from '../../common/card';
import CardTitle from '../../common/card-title';
import CardDescription from '../../common/card-description';
import KYCSteps from './kyc-steps';
import KYCLevel1 from './kyc-levels/level-1';
import KYCLevel2 from './kyc-levels/level-2';
import DialogStatus from '../../common/dialog-status';
import { useMediaQuery } from '@mui/material';
import Dialog from '../../common/dialog';
import TermsAndConditions from '../../common/terms-and-conditions';
import { KycReviewStatus } from './kyc-steps/enums/KycReviewStatus';
import { segmentIndentify, segmentKycStep1TermsConditions } from '../../../utils/analytics';

const CompanyInformation = ({
  loading,
  success,
  merchantId,
  kycLevel,
  allKycLevels,
  validKYC,
  kycReview,
  onboardingVersion,
  completedTransactions,
  name,
  lastName,
  documentType,
  document,
  phone,
  website,
  country,
  logoUrl,
  kycReviewStatus,
  showUploadLogo,
  showUpLevel,
  loadingUploadLogo,
  fnShowUploadLogo,
  fnShowUpLevel,
  fnGetKycLevels,
}) => {
  const isTabletMobile = useMediaQuery('(max-width:768px)');
  const { level, requiredTransactions } = kycLevel;

  const history = useHistory();
  const [kycComponent, setKycComponent] = useState(null);
  const [seccessMsg, setSeccessMsg] = useState(false);
  const [onboardingMenu, setOnboardingMenu] = useState([]);
  const componentRef = useRef(null);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);

  useEffect(() => {
    fnGetKycLevels();
    segmentIndentify(merchantId, country);
  }, []);

  useEffect(() => {
    if (!showUpLevel) {
      scrollIntoView(componentRef);
    }
  }, [showUpLevel]);

  useEffect(() => {
    if (success && showUpLevel) {
      setTimeout(() => {
        fnShowUpLevel(false);
        setSeccessMsg(true);
      }, 2000);
    }
  }, [success]);

  const openTermsAndConditionsHandler = () => {
    segmentKycStep1TermsConditions();
    setShowTermsAndConditions(true);
  };

  return (
    <div ref={componentRef}>
      <TopDashboardSection
        title={level === 1 ? l('companyInformation.headline.step1') : l('companyInformation.headline.step2')}
      />
      <div className="company-information">
        {level !== 4 && (
          <div className="company-information__section">
            <Card>
              {loading && <Loading fullScreem />}
              {kycReview && !kycReviewStatus && (
                <>
                  <CardTitle>{l('companyInformation.kycReview.headline')}</CardTitle>
                  <CardDescription>{l('companyInformation.kycReview.subheadline')}</CardDescription>
                </>
              )}
              {kycReview && kycReviewStatus === KycReviewStatus.REJECTED_BY_WEBSITE && (
                <>
                  <CardTitle>{l('companyInformation.kycReview.headline.website')}</CardTitle>
                  <CardDescription>{l('companyInformation.kycReview.subheadline.website')}</CardDescription>
                </>
              )}
              {kycReview && kycReviewStatus === KycReviewStatus.REJECTED_BY_INDUSTRY && (
                <>
                  <CardTitle>{l('companyInformation.kycReview.headline.industry')}</CardTitle>
                  <CardDescription>{l('companyInformation.kycReview.subheadline.industry')}</CardDescription>
                </>
              )}
              {validKYC && !kycReview && requiredTransactions > completedTransactions && (
                <div className="company-information__required-transactions">
                  <Message body={Parser(l('companyInformation.requiredTransactions', requiredTransactions))} />
                </div>
              )}
              <div className="company-information__link_docs">
                <LinkDocs textKey="linkDocs.levels" url={Urls.docs.levels} />
              </div>

              {level === 1 && !validKYC && !kycReview && (
                <>
                  <CardTitle>{l('companyInformation.headlineCard.step1')}</CardTitle>
                  <CardDescription>
                    {l('companyInformation.subheadlineCard.step1')}
                    <ButtonLink text={l('companyInformation.termsAndConditions')} onClick={openTermsAndConditionsHandler} />
                    <span>.</span>
                  </CardDescription>
                </>
              )}
              {((level === 1 && validKYC && !kycReview) || (level > 1 && level < 4 && !kycReview)) && (
                <>
                  <CardTitle>{l('companyInformation.headlineCard.step2')}</CardTitle>
                  <CardDescription>
                    {l('companyInformation.subheadlineCard.step2.part1')}
                    <ButtonLink
                      text={l('companyInformation.termsAndConditions')}
                      onClick={() => setShowTermsAndConditions(true)}
                    />
                    <span>. </span>
                    {l('companyInformation.subheadlineCard.step2.part2')}
                  </CardDescription>
                </>
              )}
              {level < 4 && (
                <KYCSteps
                  level={level}
                  validKYC={validKYC}
                  kycReview={kycReview}
                  limitStepOne={(allKycLevels?.levels && allKycLevels.levels[0]?.maxAmount) || '####'}
                  kycReviewStatus={kycReviewStatus}
                  onClick={step => {
                    setKycComponent(
                      step === 1 ? (
                        <KYCLevel1 setOnboardingMenu={menu => setOnboardingMenu(menu)} />
                      ) : (
                        <KYCLevel2 setOnboardingMenu={menu => setOnboardingMenu(menu)} />
                      )
                    );
                    fnShowUpLevel(true);
                  }}
                />
              )}
            </Card>
          </div>
        )}
        {name && (
          <div className="company-information__section">
            <BasicInfo
              loadingUploadLogo={loadingUploadLogo}
              logoUrl={logoUrl}
              name={name}
              lastName={lastName}
              documentType={documentType}
              document={document}
              phone={phone}
              website={website}
              country={country}
              showUploadLogo={showUploadLogo}
              fnShowUploadLogo={fnShowUploadLogo}
            />
          </div>
        )}
        {seccessMsg && level === 1 && validKYC && !kycReview && (
          <DialogStatus
            handleClose={() => {
              if (level === 1 && validKYC) {
                history.push(Routes.HOME);
              } else {
                setSeccessMsg(false);
              }
            }}
            status="success"
          >
            <div>
              <h1>{l('companyInformation.success.headline')}</h1>
              <p>{l('companyInformation.success.subheadline')}</p>
            </div>
          </DialogStatus>
        )}
        {seccessMsg && level === 1 && kycReview && (
          <DialogStatus handleClose={() => setSeccessMsg(false)} status="warning">
            <div>
              <h1>{l('companyInformation.kycReview.headline')}</h1>
              <p>{l('companyInformation.kycReview.subheadline')}</p>
            </div>
          </DialogStatus>
        )}
        {showUpLevel && kycComponent && (
          <OnboardingDialog
            dividers={false}
            unpaddingContent
            unpaddingFooter
            fullScreenMobile
            type="md"
            handleClose={() => {
              if (!loading) {
                setKycComponent(<div />);
                fnShowUpLevel(false);
              }
            }}
            title={
              isTabletMobile ? (
                <div className="">
                  <div>
                    <b>{l('companyInformation.onboarding.title.part1')}</b>
                  </div>
                  <div>{l('companyInformation.onboarding.title.part2')}</div>
                </div>
              ) : (
                <>
                  <b>{l('companyInformation.onboarding.title.part1')}</b> | {l('companyInformation.onboarding.title.part2')}
                </>
              )
            }
            body={success ? <Loading fullScreem /> : kycComponent}
            menuItems={onboardingMenu}
          />
        )}
        <Dialog
          open={showTermsAndConditions}
          type="lg"
          dividers={false}
          fullScreenMobile
          titleCenter
          handleClose={() => setShowTermsAndConditions(false)}
          body={<TermsAndConditions />}
        />
      </div>
    </div>
  );
};

CompanyInformation.propTypes = {
  loading: PropTypes.bool,
  kycLevel: PropTypes.object,
  validKYC: PropTypes.bool,
  kycReview: PropTypes.bool,
  completedTransactions: PropTypes.number,
  name: PropTypes.string,
  lastName: PropTypes.string,
  documentType: PropTypes.string,
  document: PropTypes.string,
  phone: PropTypes.string,
  website: PropTypes.string,
  logoUrl: PropTypes.string,
  kycReviewStatus: PropTypes.oneOf(['REJECTED_BY_WEBSITE', 'REJECTED_BY_INDUSTRY', 'REJECTED_BY_DOCUMENTATION', null]),
  showUploadLogo: PropTypes.bool,
  showUpLevel: PropTypes.bool,
  success: PropTypes.bool,
  loadingUploadLogo: PropTypes.bool,
  allKycLevels: PropTypes.object,
  fnShowUploadLogo: PropTypes.func,
  fnShowUpLevel: PropTypes.func,
  fnGetKycLevels: PropTypes.func,
};

const mapStateToProps = state => ({
  loading: state.companyInformation.loading,
  showUploadLogo: state.companyInformation.uploadLogo.showModal,
  showUpLevel: state.companyInformation.showUpLevel,
  success: state.companyInformation.success,
  loadingUploadLogo: state.companyInformation.uploadLogo.loading,
  merchantId: state.dashboard.merchantMe?.id,
  kycLevel: state.dashboard.merchantMe?.kycLevel,
  validKYC: state.dashboard.merchantMe?.validKYC,
  kycReview: state.dashboard.merchantMe?.kycReview,
  onboardingVersion: state.dashboard.merchantMe?.onboardingVersion,
  completedTransactions: state.dashboard.merchantMe?.completedTransactions,
  name: state.dashboard.merchantMe?.name,
  lastName: state.dashboard.merchantMe?.lastName,
  documentType: state.dashboard.merchantMe?.documentType,
  document: state.dashboard.merchantMe?.document,
  phone: state.dashboard.merchantMe?.phone,
  website: state.dashboard.merchantMe?.website,
  country: state.dashboard.merchantMe?.country,
  logoUrl: state.dashboard.merchantMe?.logoUrl,
  kycReviewStatus: state.dashboard.merchantMe?.kycReviewStatus,
  allKycLevels: state.companyInformation.allKycLevels,
});

const mapDispatchToProps = dispatch => ({
  fnShowUploadLogo: show => dispatch(showUploadLogo(show)),
  fnShowUpLevel: show => dispatch(changeShowUpLevel(show)),
  fnGetKycLevels: () => dispatch(getKycLevels()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyInformation);
